import { Button, Stack, TextField, Typography } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { useMutation } from '@tanstack/react-query';
import React, { ChangeEvent, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import { useConfig } from '../../../hooks/useConfig';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { postRequestJSON } from '../../../util/services';

export const SUCCESS_MESSAGE = 'Location updates are being processed in background. Check your email for status.';

function LocationRadii() {
  const { apiHost } = useConfig();
  const [radiusScale, setRadiusScale] = useState('');
  const [radius, setRadius] = useState('');
  const { getValues } = useFormContext();
  const { showSnackBar } = useSnackBar();

  const radiusScaleMutation = useMutation({
    mutationFn: (data: { campaign_id: number; radius_scale_percent: string }) =>
      postRequestJSON(apiHost, `/admin/campaigns/${data.campaign_id}/locations/radius_scale`, data),
    onSuccess: (data) => {
      if (!data.errors) {
        showSnackBar(SUCCESS_MESSAGE, 'success', { event: 'location_radius_scaled' });
      }
    },
    onError: () => {
      showSnackBar('Failed to save radius scale', 'error', { event: 'location_radius_scale_error' });
    },
  });

  const radiusMutation = useMutation({
    mutationFn: (data: { campaign_id: number; radius_set: string }) =>
      postRequestJSON(apiHost, `/admin/campaigns/${data.campaign_id}/locations/radius_set`, data),
    onSuccess: (data) => {
      if (!data.errors) {
        showSnackBar(SUCCESS_MESSAGE, 'success', { event: 'location_radius_updated' });
      }
    },
    onError: () => {
      showSnackBar('Failed to save radius', 'error', { event: 'location_radius_update_error' });
    },
  });

  const handleSetRadiusScaleBtnClick = async () => {
    const { id } = getValues();
    radiusScaleMutation.mutate({ campaign_id: id, radius_scale_percent: radiusScale });
  };

  const handleSetRadiusBtnClick = async () => {
    const { id } = getValues();
    radiusMutation.mutate({ campaign_id: id, radius_set: radius });
  };

  const radiusScaleErrors = radiusScaleMutation.isSuccess && radiusScaleMutation.data.errors;
  const radiusErrors = radiusMutation.isSuccess && radiusMutation.data.errors;

  return (
    <Stack direction="row" spacing={2}>
      <Stack>
        <Typography marginBottom={2}>Scale all location radii to:</Typography>
        <Stack direction="row" alignItems="baseline" spacing={1} marginBottom={2}>
          <NumericFormat
            value={radiusScale}
            variant="standard"
            size="small"
            onChange={(e: ChangeEvent<HTMLInputElement>) => setRadiusScale(e.target.value)}
            customInput={TextField}
            label="Percentage of original value"
            placeholder="0"
            allowNegative={false}
            decimalScale={0}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>%</Typography>
                </InputAdornment>
              ),
            }}
            error={Boolean(radiusScaleErrors)}
            helperText={radiusScaleErrors || ''}
          />

          <Button
            variant="outlined"
            size="large"
            onClick={handleSetRadiusScaleBtnClick}
            disabled={radiusScaleMutation.isLoading}
          >
            Scale
          </Button>
        </Stack>
      </Stack>
      <Stack>
        <Typography marginBottom={2}>Set all location radii to:</Typography>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <NumericFormat
            autoComplete="on"
            value={radius}
            variant="standard"
            size="small"
            onChange={(e: ChangeEvent<HTMLInputElement>) => setRadius(e.target.value)}
            customInput={TextField}
            label="Distance"
            placeholder="0"
            allowNegative={false}
            decimalScale={0}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>m</Typography>
                </InputAdornment>
              ),
            }}
            error={Boolean(radiusErrors)}
            helperText={radiusErrors || ''}
          />
          <Button variant="outlined" size="large" onClick={handleSetRadiusBtnClick} disabled={radiusMutation.isLoading}>
            Set
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default LocationRadii;
